import { LoadingButton } from '@mui/lab';
import {
  Grid,
  MenuItem,
  Select,
  Typography,
  LinearProgress,
  IconButton,
  CardMedia,
  Hidden,
  Badge,
  Tooltip,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Box } from '@mui/system';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink } from 'react-router-dom';
import {
  Distributor,
  getDistributorsListForFilters,
  getRMsList,
} from '../../redux-store/actions';
import { fundAccess, getFundsList } from '../../redux-store/actions/funds';
import {
  distributorOnboardingCompletedDownload,
  distributorWiseCommitmentAmountDownload,
  distributorWiseOnBoardingSummaryDownload,
  getCompletedApplications,
  getDistributorWiseCommitmentAmount,
  getDistributorWiseOnBoardingSummary,
  getOnBoardingDetails,
  getOnBoardingSummary,
  onBoardingDetailsDownload,
  onBoardingDetailsDownloadAllFields,
  onBoardingSummaryDownload,
} from '../../redux-store/actions/reports';
import {
  DistributorCommitmentAmountReportTypeSummary,
  GenericReportTypeSummary,
  GetDistributorCommitmentAmount,
  GetOnboardingCompletedDetailsReportResponse,
  GetOnboardingDetailsReportResponse,
  GetOnBoardingSummaryReportResponse,
  OnBoardingCompletedReportDetailsType,
  OnBoardingReportDetailsType,
  ReportFilterType,
  RMType,
} from '../../redux-store/types/api-types';
import { accessType, FundProps, GetFundsListResponseBody } from '../../redux-store/types/funds';
import { CommonLayout } from '../commonComponents';
import { ColumnType, DataTable } from '../DataTable';
import { ListPaginationBar } from '../common/ListPagination';
import { FilterDatePicker, FilterDropdown, initialFilters } from './filters';
import {
  applicationTypes,
  filterApplicationStatusTypes,
  filterApplicationTypes,
  getFromDate,
  onboardingDetailsDownload,
  reportData,
  reportDownload,
} from './report-utils';
import { formatShortDate } from '../../utils/date';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { USER_ROLES } from '../../utils/constant';
import { RootStateType } from '../../redux-store/reducers';

const tableMainBoxStyles = {
  maxHeight: 'none',
  minHeight: 'unset',
  overflow: 'visible',
};

const FundStatusPill = ({ frozen }: { frozen: boolean }): JSX.Element => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.5,
      py: 0.5,
      borderRadius: '9999px',
      fontFamily: 'Inter, sans-serif',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      bgcolor: frozen ? '#F3F4F6' : '#ECFDF5',
      color: frozen ? '#374151' : '#027A48',
    }}>
    {frozen ? 'Freezed' : 'Active'}
  </Box>
);

function CommitmentFundAccordion({
  name,
  tableHeaders,
  tableData,
}: {
  name: string;
  tableHeaders: ColumnType[];
  tableData: unknown[];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: '12px',
        border: '1px solid #E8ECF0',
        mb: 1.5,
        overflow: 'hidden',
      }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          border: 'none',
          bgcolor: 'transparent',
          px: 2,
          py: 1.75,
          cursor: 'pointer',
          textAlign: 'left',
        }}>
        <KeyboardArrowDownIcon
          sx={{
            color: '#2057A6',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
        <Typography
          sx={{
            flex: 1,
            fontFamily: 'Inter, sans-serif',
            fontSize: 15,
            fontWeight: 600,
            color: '#2057A6',
          }}>
          {name}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 2 }}>
          <DataTable
            isLoading={false}
            tableHeader={tableHeaders}
            tableData={tableData}
            boxStyles={tableMainBoxStyles}
            tableHeaderCustomStyles={{
              '.MuiTableCell-head': {
                backgroundColor: '#F6F9FD',
                color: '#1F53BE',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

const CommonComponentForSummaryReports = ({
  isLoading,
  tableHeaders,
  summaryReport,
  funds = [],
}: {
  isLoading: boolean;
  tableHeaders: ColumnType[];
  summaryReport: GetOnBoardingSummaryReportResponse[];
  funds?: FundProps[];
}): JSX.Element => {
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getApplicationStatusCount = (
    applications: GenericReportTypeSummary[],
    status: string
  ): string => {
    return applications.find((application) => application.status === status)?.count || '0';
  };

  const buildTableData = (report: GetOnBoardingSummaryReportResponse) =>
    Object.keys(report)
      .filter((key) => key !== 'name')
      .map((key) => {
        const applicationType = applicationTypes
          .map((type) => {
            if (type.key === key) {
              return type.value;
            }
            return;
          })
          .filter((ele) => ele)
          .toString();
        const applications = report[key] as GenericReportTypeSummary[];
        if (applications.length) {
          return {
            investorType: applicationType,
            total_onboarded: getApplicationStatusCount(applications, 'total_onboarded'),
            in_progress: getApplicationStatusCount(applications, 'in_progress'),
            draft: getApplicationStatusCount(applications, 'draft'),
            pending_with_investor: getApplicationStatusCount(applications, 'pending_with_investor'),
            pending_with_im: getApplicationStatusCount(applications, 'pending_with_fundmanager'),
            pending_with_trustee: getApplicationStatusCount(applications, 'pending_with_trustee'),
            invitationexpired: getApplicationStatusCount(applications, 'invitationexpired'),
            rejected: getApplicationStatusCount(applications, 'rejected'),
            completed: getApplicationStatusCount(applications, 'completed'),
          };
        }
        return;
      })
      .filter((ele) => ele);

  const noOfPages = Math.max(1, Math.ceil(summaryReport.length / rowsPerPage) || 1);
  const pagedReports = summaryReport.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  useEffect(() => {
    if (currentPage > noOfPages) setCurrentPage(1);
  }, [noOfPages, currentPage]);

  useEffect(() => {
    const start = (currentPage - 1) * rowsPerPage;
    setExpandedName(summaryReport[start]?.name || null);
  }, [currentPage, rowsPerPage, summaryReport]);

  return (
    <Box>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <>
          {pagedReports.map((report) => {
            const tableData = buildTableData(report);
            const isExpanded = expandedName === report.name;
            const matchedFund = funds.find((f) => f.schemeName === report.name);
            const frozen = matchedFund
              ? !matchedFund.isActive || !!matchedFund.freezeStatus
              : false;
            return (
              <Box
                key={report.name}
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E8ECF0',
                  mb: 1.5,
                  overflow: 'hidden',
                }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setExpandedName(isExpanded ? null : report.name)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    border: 'none',
                    bgcolor: 'transparent',
                    px: 2,
                    py: 1.75,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}>
                  <KeyboardArrowDownIcon
                    sx={{
                      color: '#2057A6',
                      transform: isExpanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 15,
                      fontWeight: 600,
                      color: '#2057A6',
                    }}>
                    {report.name}
                  </Typography>
                  <FundStatusPill frozen={frozen} />
                </Box>
                {isExpanded && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <DataTable
                      isLoading={false}
                      tableHeader={tableHeaders}
                      tableData={tableData}
                      boxStyles={tableMainBoxStyles}
                      tableHeaderCustomStyles={{
                        '.MuiTableCell-head': {
                          backgroundColor: '#F6F9FD',
                          color: '#1F53BE',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
          {summaryReport.length > 0 && (
            <ListPaginationBar
              pageCount={noOfPages}
              page={currentPage}
              onPageChange={(_e, value) => setCurrentPage(value)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setCurrentPage(1);
              }}
              total={summaryReport.length}
            />
          )}
        </>
      )}
    </Box>
  );
};

const OnBoardingSummaryReport = ({
  tableHeaders,
  filters,
  funds,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
  funds: FundProps[];
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetOnBoardingSummaryReportResponse[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getOnBoardingSummary(filters)
        )) as unknown as GetOnBoardingSummaryReportResponse[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <CommonComponentForSummaryReports
      isLoading={isLoading}
      tableHeaders={tableHeaders}
      summaryReport={summaryReport}
      funds={funds}
    />
  );
};

const OnBoardingDetailsReport = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<OnBoardingReportDetailsType[]>([]);
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(1);

  const apiCall = async (page: number) => {
    setIsLoading(true);
    const { data = [], pages = 0 } = (await dispatch(
      getOnBoardingDetails({ ...filters, page, limit: 10 })
    )) as unknown as GetOnboardingDetailsReportResponse;
    return { data, pages };
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        const { data, pages } = await apiCall(1);
        if (!isComponentActive) return;
        setTableData(data);
        setNoPages(pages);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  const handlePageChange = async (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const { data, pages } = await apiCall(value);
    setTableData(data);
    setNoPages(pages);
    setIsLoading(false);
  };

  return (
    <DataTable
      isLoading={isLoading}
      tableHeader={tableHeaders}
      tableData={tableData}
      boxStyles={tableMainBoxStyles}
      noOfPages={noOfPages}
      currentPage={currentPage}
      paginationAction={handlePageChange}
    />
  );
};

const DistributorOnBoardingSummary = ({
  tableHeaders,
  filters,
  funds,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
  funds: FundProps[];
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetOnBoardingSummaryReportResponse[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getDistributorWiseOnBoardingSummary(filters)
        )) as unknown as GetOnBoardingSummaryReportResponse[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <CommonComponentForSummaryReports
      isLoading={isLoading}
      tableHeaders={tableHeaders}
      summaryReport={summaryReport}
      funds={funds}
    />
  );
};

const DistributorCommitmentAmount = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryReport, setSummaryReport] = useState<GetDistributorCommitmentAmount[]>([]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        setIsLoading(true);
        const response = (await dispatch(
          getDistributorWiseCommitmentAmount(filters)
        )) as unknown as GetDistributorCommitmentAmount[];
        if (!isComponentActive) return;
        setSummaryReport(response);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  return (
    <>
      {' '}
      <Typography
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}>
        <InfoOutlinedIcon color="info" fontSize="small" />
        <Typography
          sx={{
            fontSize: 15,
            ml: 1,
          }}>
          Data is shown only for completed applications .
        </Typography>
      </Typography>
      {isLoading ? (
        <LinearProgress />
      ) : (
        summaryReport.map((report) => {
          const tableData = Object.keys(report)
            .filter((key) => key !== 'name')
            .map((key) => {
              const applicationType = applicationTypes
                .map((type) => {
                  if (type.key === key) {
                    return type.value;
                  }
                  return;
                })
                .filter((ele) => ele)
                .toString();
              const applications = report[key] as DistributorCommitmentAmountReportTypeSummary;
              if (Object.keys(report[key]).length === 0) {
                return;
              }
              return {
                investorType: applicationType,
                completed: applications.completed,
                commitmentAmount: applications.commitmentAmount,
                contributionAmount: applications.contributionAmount,
              };
            })
            .filter((ele) => ele);
          return (
            <CommitmentFundAccordion
              key={report.name}
              name={report.name}
              tableHeaders={tableHeaders}
              tableData={tableData}
            />
          );
        })
      )}
    </>
  );
};

const OnBoardingCompletedDetailsReport = ({
  tableHeaders,
  filters,
}: {
  tableHeaders: ColumnType[];
  filters: ReportFilterType;
}): JSX.Element => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<OnBoardingCompletedReportDetailsType[]>([]);
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(1);

  const apiCall = async (page: number) => {
    setIsLoading(true);
    const { data = [], pages = 0 } = (await dispatch(
      getCompletedApplications({ ...filters, page })
    )) as unknown as GetOnboardingCompletedDetailsReportResponse;
    return { data, pages };
  };

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        const { data, pages } = await apiCall(1);
        if (!isComponentActive) return;
        setTableData(data);
        setNoPages(pages);
      } catch (e) {
        console.error(e);
      } finally {
        if (isComponentActive) setIsLoading(false);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, [filters]);

  const handlePageChange = async (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
    const { data, pages } = await apiCall(value);
    setTableData(data);
    setNoPages(pages);
    setIsLoading(false);
  };

  return (
    <>
      {' '}
      <Typography
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
        }}>
        <InfoOutlinedIcon color="info" fontSize="small" />
        <Typography
          sx={{
            fontSize: 15,
            ml: 1,
            mb: 2,
          }}>
          Data is shown only for completed applications .
        </Typography>
      </Typography>
      <DataTable
        isLoading={isLoading}
        tableHeader={tableHeaders}
        tableData={tableData}
        boxStyles={tableMainBoxStyles}
        noOfPages={noOfPages}
        currentPage={currentPage}
        paginationAction={handlePageChange}
      />
    </>
  );
};

export default function Reports(): JSX.Element {
  const dispatch = useDispatch();
  const [reportType, setReportType] = useState(reportData[0].value);
  const [filters, setFilters] = useState<ReportFilterType>(initialFilters);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<boolean>(false);
  const open = Boolean(filtersAnchorEl);
  const [funds, setFunds] = useState<FundProps[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [toDateErrorText, setToDateErrorText] = useState('');
  const [fromDateErrorText, setFromDateErrorText] = useState('');
  const [rmsList, setRmList] = useState<RMType[]>([]);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [access, setAccess] = useState(false);
  const { role = '' } = useSelector((store: RootStateType) => store.auth);

  useEffect(() => {
    let isComponentActive = true;
    (async function () {
      try {
        //super Admin Access same for creating funds and viewing completed applications report
        if (USER_ROLES.AMCAPPROVER !== role) {
          const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
          setAccess(fundSetupAccess);
        }
        const { funds } = (await dispatch(getFundsList())) as unknown as GetFundsListResponseBody;
        const response = (await dispatch(
          getDistributorsListForFilters()
        )) as unknown as Distributor[];
        const rms = (await dispatch(getRMsList())) as unknown as RMType[];
        if (!isComponentActive) return;
        setFunds(funds);
        setDistributors(response);
        setRmList(rms);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      isComponentActive = false;
    };
  }, []);

  // useEffect(() => {
  //   setFilters(initialFilters);
  // }, [reportType]);

  const reportsTable = useMemo(() => {
    const tableHeaders = reportData.find(({ value }) => value === reportType)?.tableHeaders || [];
    const updatedFilter = {
      ...filters,
      status: filters?.status
        ?.toString()
        .replace('draft_send_back', 'draft')
        .replace('draft,draft', 'draft'),
      applicationSentBack:
        filters?.status.includes('draft_send_back') && !filters?.status.includes('draft')
          ? true
          : null,
    };
    switch (reportType) {
      case 'onboardingSummary':
        return (
          <OnBoardingSummaryReport tableHeaders={tableHeaders} filters={updatedFilter} funds={funds} />
        );
      case 'onboardingDetails':
        return <OnBoardingDetailsReport tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'distributorOnBoardingSummary':
        return (
          <DistributorOnBoardingSummary
            tableHeaders={tableHeaders}
            filters={updatedFilter}
            funds={funds}
          />
        );
      case 'distributorCommitmentAmount':
        return <DistributorCommitmentAmount tableHeaders={tableHeaders} filters={updatedFilter} />;
      case 'onboardingCompletedDetails':
        return (
          <OnBoardingCompletedDetailsReport tableHeaders={tableHeaders} filters={updatedFilter} />
        );
    }
  }, [reportType, filters, funds]);

  const handleFiltersAnchorEl = () => {
    setFiltersAnchorEl(true);
  };

  const handleFiltersAnchorElClose = () => {
    setFiltersAnchorEl(false);
  };

  const filterOptions = (): JSX.Element => {
    const badgeCount = ['distributorId', 'schemeId', 'planId', 'rmId', 'investorType'].filter(
      (filterKey) => filters[filterKey]
    ).length;
    const updatedBadgeCountWithStatusFilter =
      filters.status?.length > 0 ? badgeCount + 1 : badgeCount;
    return (
      <>
        <IconButton
          sx={{ p: 0.5 }}
          id="filter-icon-button"
          aria-controls="filter-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleFiltersAnchorEl}>
          <Badge badgeContent={updatedBadgeCountWithStatusFilter} color="info">
            <CardMedia component="img" image="/images/filter-icon.svg" alt="Filter Icon" />
          </Badge>
        </IconButton>{' '}
        {filtersAnchorEl && (
          <FilterDropdown
            filters={filters}
            onFiltersChange={(value) => setFilters(value)}
            anchorEl={filtersAnchorEl}
            handleClose={handleFiltersAnchorElClose}
            funds={funds}
            distributors={distributors}
            rmsList={rmsList}
          />
        )}
      </>
    );
  };

  const selectedFilterChips = useMemo(() => {
    const chips: { key: string; label: string; value: string; onClear: () => void }[] = [];
    const reportLabel = reportData.find((r) => r.value === reportType)?.key;
    if (reportLabel) {
      chips.push({
        key: 'reportType',
        label: 'Report Type',
        value: reportLabel,
        onClear: () => setReportType(reportData[0].value),
      });
    }
    if (filters.from) {
      chips.push({
        key: 'from',
        label: 'From Date',
        value: formatShortDate(filters.from),
        onClear: () => {
          setFromDateErrorText('');
          setToDateErrorText('');
          setFilters((prev) => ({ ...prev, from: getFromDate() }));
        },
      });
    }
    if (filters.to) {
      chips.push({
        key: 'to',
        label: 'To Date',
        value: formatShortDate(filters.to),
        onClear: () => {
          setFromDateErrorText('');
          setToDateErrorText('');
          setFilters((prev) => ({ ...prev, to: new Date().toISOString() }));
        },
      });
    }
    if (filters.investorType && filters.investorType !== 'all') {
      const investorLabel =
        filterApplicationTypes.find((t) => t.key === filters.investorType)?.value ||
        String(filters.investorType);
      chips.push({
        key: 'investorType',
        label: 'Investor Type',
        value: investorLabel,
        onClear: () => setFilters((prev) => ({ ...prev, investorType: 'all' })),
      });
    }
    if (filters.distributorId) {
      const dist = distributors.find((d) => Number(d.id) === Number(filters.distributorId));
      chips.push({
        key: 'distributorId',
        label: 'Distributor',
        value: dist?.name || String(filters.distributorId),
        onClear: () => setFilters((prev) => ({ ...prev, distributorId: null })),
      });
    }
    if (filters.schemeId) {
      const scheme = funds.find((f) => String(f.id) === String(filters.schemeId));
      chips.push({
        key: 'schemeId',
        label: 'Scheme',
        value: scheme?.schemeName || String(filters.schemeId),
        onClear: () => setFilters((prev) => ({ ...prev, schemeId: '', planId: '' })),
      });
    }
    if (filters.planId) {
      const plan = funds
        .flatMap((f) => f.plans || [])
        .find((p) => String(p.id) === String(filters.planId));
      chips.push({
        key: 'planId',
        label: 'Plan',
        value: plan?.planDescription || plan?.planCode || String(filters.planId),
        onClear: () => setFilters((prev) => ({ ...prev, planId: '' })),
      });
    }
    if (filters.rmId) {
      const rm = rmsList.find((r) => String(r.id) === String(filters.rmId));
      chips.push({
        key: 'rmId',
        label: 'RM',
        value: rm?.name || rm?.user?.name || String(filters.rmId),
        onClear: () => setFilters((prev) => ({ ...prev, rmId: '' })),
      });
    }
    if (filters.status && (Array.isArray(filters.status) ? filters.status.length : filters.status)) {
      const statusValues = Array.isArray(filters.status) ? filters.status : [filters.status];
      const statusLabel = statusValues
        .map(
          (s) =>
            filterApplicationStatusTypes?.find?.((t: { key: string; value: string }) => t.key === s)
              ?.value || s
        )
        .join(', ');
      chips.push({
        key: 'status',
        label: 'Status',
        value: statusLabel,
        onClear: () => setFilters((prev) => ({ ...prev, status: '' })),
      });
    }
    return chips;
  }, [reportType, filters, distributors, funds, rmsList]);

  const handleClearAllSelectedFilters = () => {
    setFilters(initialFilters);
    setReportType(reportData[0].value);
  };

  return (
    <>
      <CommonLayout>
        <>
          <Box sx={{ px: { xs: 2, sm: 0 } }}>
            <Breadcrumbs
              separator=">"
              aria-label="breadcrumb"
              sx={{
                mb: 1.5,
                '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
                '.MuiTypography-root, .MuiLink-root': {
                  fontSize: 14,
                  color: '#111927',
                  fontFamily: 'Inter, sans-serif',
                },
              }}>
              <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
                Home
              </Link>
              <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>Reports</Typography>
            </Breadcrumbs>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 700,
                color: '#092056',
                mb: 2.5,
              }}>
              Reports
            </Typography>
            <Box>
              <Typography
                sx={{
                  display: 'block',
                  padding: '0 0 5px',
                  fontWeight: 'bold',
                  alignSelf: { xs: 'center' },
                }}>
                Report Type
              </Typography>
              <Grid container>
                <Grid item xs={10} sm={10} md={3}>
                  <Select
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '0 10px',
                      borderRadius: '6px',
                      fontSize: '15px',
                    }}
                    value={reportType}
                    onChange={({ target: { value } }) => {
                      // setFilters(initialFilters);
                      setReportType(value);
                    }}>
                    {reportData
                      .filter((reports) => {
                        if (!access && reports.value === 'onboardingCompletedDetails') {
                          return;
                        }
                        return reports;
                      })
                      .filter((ele) => ele)
                      .map((report) => (
                        <MenuItem value={report.value} key={report.key}>
                          {report.key}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>
                <Hidden mdUp={true}>
                  <Grid item xs={2}>
                    {filterOptions()}
                  </Grid>
                </Hidden>
                <Grid item xs={6} sm={6} md={4}>
                  <Box
                    sx={{
                      display: { sm: 'block', md: 'flex' },
                      color: 'blue',
                      alignItems: 'center',
                      padding: '0 10px',
                      paddingLeft: { xs: '0px', sm: '0px', md: '10px' },
                      marginTop: { xs: '10px', sm: '10px', md: '0px' },
                    }}>
                    <Typography style={{ fontSize: '13px', color: 'blue' }}>From Date</Typography>
                    <FilterDatePicker
                      id="reports-from-date-picker"
                      placeholder={'From Date'}
                      onChange={(date) => {
                        const timeDiff =
                          new Date(date as Date).getTime() - new Date(filters.to).getTime();
                        if (timeDiff > 0) {
                          setFromDateErrorText('From Date should be less than To Date');
                          return;
                        }
                        setFilters({ ...filters, from: new Date(date as Date).toISOString() });
                        setFromDateErrorText('');
                      }}
                      value={new Date(filters.from)}
                      errorText={fromDateErrorText}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                  <Box
                    sx={{
                      display: { sm: 'block', md: 'flex' },
                      color: 'blue',
                      alignItems: 'center',
                      padding: '0 10px',
                      marginTop: { xs: '10px', sm: '10px', md: '0px' },
                    }}>
                    <Typography style={{ fontSize: '13px', color: 'blue' }}>To Date</Typography>
                    <FilterDatePicker
                      id="reports-to-date-picker"
                      placeholder={'To Date'}
                      value={new Date(filters.to)}
                      onChange={(date) => {
                        const timeDiff =
                          new Date(filters.from).getTime() - new Date(date as Date).getTime();
                        if (timeDiff > 0) {
                          setToDateErrorText('To Date should be greater than From Date');
                          return;
                        }
                        setFilters({ ...filters, to: new Date(date as Date).toISOString() });
                        setToDateErrorText('');
                      }}
                      errorText={toDateErrorText}
                    />
                  </Box>
                </Grid>
                <Hidden only={['xs', 'sm']}>
                  <Grid item md={1}>
                    {filterOptions()}
                  </Grid>
                </Hidden>
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              <Tooltip title="Click here to download Report">
                {reportType !== 'onboardingDetails' ? (
                  <LoadingButton
                    loadingPosition="start"
                    type="submit"
                    loading={isSendLoading}
                    variant="contained"
                    sx={{
                      fontSize: 12,
                      minWidth: isSendLoading ? 180 : '',
                      letterSpacing: 1,
                      fontWeight: 400,
                      px: isSendLoading ? 0 : 3,
                      py: 1,
                      mt: 2,
                      mr: 4,
                    }}
                    onClick={async () => {
                      try {
                        setIsSendLoading(true);
                        const download = { ...filters, downloadFile: true };
                        switch (reportType) {
                          case 'onboardingSummary': {
                            const res = await dispatch(onBoardingSummaryDownload(download));
                            reportDownload(res as unknown as Blob, 'aif-onboarding-summary.xlsx');
                            return;
                          }
                          case 'distributorOnBoardingSummary': {
                            const res = await dispatch(
                              distributorWiseOnBoardingSummaryDownload(download)
                            );
                            reportDownload(res as unknown as Blob, 'aif-onboarding-summary.xlsx');
                            return;
                          }
                          case 'distributorCommitmentAmount': {
                            const res = await dispatch(
                              distributorWiseCommitmentAmountDownload(download)
                            );
                            reportDownload(
                              res as unknown as Blob,
                              'aif-distributor-commitment-amount.xlsx'
                            );
                            return;
                          }
                          case 'onboardingCompletedDetails': {
                            const res = await dispatch(
                              distributorOnboardingCompletedDownload(download)
                            );
                            reportDownload(res as unknown as Blob, 'aif-onboarding-completed.xlsx');
                            return;
                          }
                        }
                      } catch (e) {
                        setIsSendLoading(false);
                        console.error((e as Error).message);
                      } finally {
                        setIsSendLoading(false);
                      }
                    }}>
                    Download Report
                  </LoadingButton>
                ) : (
                  <Grid item xs={10} sm={10} md={3} sx={{ mt: 2, mr: 4 }}>
                    <Select
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '0 10px',
                        borderRadius: '6px',
                        fontSize: '15px',
                      }}
                      defaultValue={'Export'}
                      onChange={async ({ target: { value } }) => {
                        try {
                          const download = { ...filters, downloadFile: true };
                          if (value === 'Download Current Fields') {
                            const res = await dispatch(onBoardingDetailsDownload(download));
                            reportDownload(
                              res as unknown as Blob,
                              'aif-onboarding-details-current-fields.xlsx'
                            );
                          } else if (value === 'Download All Fields') {
                            const res = await dispatch(
                              onBoardingDetailsDownloadAllFields(download)
                            );
                            reportDownload(
                              res as unknown as Blob,
                              'aif-onboarding-details-all-fields.xlsx'
                            );
                          }
                        } catch (e) {
                          setIsSendLoading(false);
                          console.error((e as Error).message);
                        }
                      }}>
                      {onboardingDetailsDownload.map((report) => (
                        <MenuItem value={report} key={report}>
                          {report}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}
              </Tooltip>
            </Box>
            <Box
              sx={{
                mt: { sm: 4, xs: 3 },
              }}>
              {selectedFilterChips.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    mb: 2.5,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      flexWrap: 'wrap',
                      flex: 1,
                      border: '1px dashed #D0D5DD',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 1.25,
                      bgcolor: '#FFFFFF',
                    }}>
                    {selectedFilterChips.map((chip) => (
                      <Box
                        key={chip.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#111927',
                          }}>
                          {chip.label}:
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            bgcolor: '#F2F4F7',
                            borderRadius: '9999px',
                            px: 1.25,
                            py: 0.5,
                          }}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: 13,
                              fontWeight: 400,
                              color: '#6B7280',
                            }}>
                            {chip.value}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${chip.label} filter`}
                            onClick={chip.onClear}
                            sx={{
                              p: 0.15,
                              bgcolor: '#6B7280',
                              color: '#FFFFFF',
                              '&:hover': { bgcolor: '#4B5563' },
                              width: 16,
                              height: 16,
                            }}>
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                    onClick={handleClearAllSelectedFilters}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 14,
                      fontWeight: 500,
                      textTransform: 'none',
                      color: '#D92D20',
                      minWidth: 'unset',
                      px: 1,
                      '&:hover': { bgcolor: 'rgba(217, 45, 32, 0.04)' },
                    }}>
                    Clear
                  </Button>
                </Box>
              )}
              {reportsTable}
            </Box>
          </Box>
        </>
      </CommonLayout>
    </>
  );
}
